/**
 * V113-7 — small shared pieces of the Monitoring screens: status chips (word + glyph), the copy field (copies the
 * value it was given, says so on screen when the clipboard refuses), the Verify disclosure, a role-gated button
 * ("Ask <owner>" under a disabled control), and the New / In progress / Due lines (D-113-31).
 */
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button, StatusGlyph, Toast, type StatusGlyphKind } from '@aigovops/design-system';
import type { ConfigChangeCause, PackValidityStatus, SystemPackState } from '@aigovops/core';
import type { Capability } from '../../lib/monitoring-capabilities';
import { listWorkspaceMembers } from '../../lib/api';
import { monitoringErrorOf } from '../../lib/monitoring-api';
import { startSweep } from '../../lib/sweep-api';
import { fmtDate } from '../../lib/format';
import {
  causeText, copyText, dueText, inProgressLinkLabel, inProgressText, newMarkerText, packMeta, type ChipTone,
} from '../../lib/monitoring-view';

export function StatusChip({ label, tone = 'neutral', glyph, id, large }: { label: string; tone?: ChipTone; glyph?: StatusGlyphKind; id?: string; large?: boolean }) {
  return (
    <span id={id} className={`gp-chip gp-chip--${tone} mon-chip${large ? ' mon-chip--lg' : ''}`}>
      {glyph ? <StatusGlyph kind={glyph} size={12} /> : null}
      {label}
    </span>
  );
}

export function PackChip({ state, large, id }: { state: PackValidityStatus | SystemPackState | null | undefined; large?: boolean; id?: string }) {
  const m = packMeta(state);
  return <StatusChip id={id} label={m.label} tone={m.tone} glyph={m.glyph} large={large} />;
}

/**
 * A value with a Copy button. `value` is what gets copied (kept in `data-value`, never read back from the rendered
 * text); `display` is what shows (defaults to the value). A refused clipboard says so and selects the text.
 */
export function CopyField({ label, value, display, onCopied, hideLabel }: {
  label: string; value: string; display?: string; onCopied?: () => void; hideLabel?: boolean;
}) {
  const id = useId();
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  return (
    <div className="mon-col">
      <span id={`${id}-l`} className={hideLabel ? 'gp-sr-only' : 'mon-eyebrow'}>{label}</span>
      <div className="mon-copy">
        <code id={`${id}-v`} className="mon-copy-value" data-value={value} aria-labelledby={`${id}-l`}>{display ?? value}</code>
        <Button compact aria-label={`Copy ${label}`} aria-describedby={`${id}-l`} onClick={async () => {
          const el = document.getElementById(`${id}-v`);
          const ok = await copyText(el?.getAttribute('data-value') ?? value);
          setState(ok ? 'copied' : 'failed');
          if (ok) onCopied?.();
          else if (el) {
            const sel = window.getSelection?.();
            const range = document.createRange?.();
            if (sel && range) { range.selectNodeContents(el); sel.removeAllRanges(); sel.addRange(range); }
          }
        }}>{state === 'copied' ? 'Copied' : 'Copy'}</Button>
      </div>
      <p role="status" aria-live="polite" className={state === 'failed' ? 'mon-err' : 'gp-sr-only'}>
        {state === 'failed' ? 'Copy didn’t work here. The text is selected; copy it with your keyboard.' : state === 'copied' ? `${label} copied` : ''}
      </p>
    </div>
  );
}

/** A Verify disclosure; `context` gives each one a distinct accessible name ("Verify · Prompt-injection guardrail"). */
export function VerifyDisclosure({ children, label = 'Verify', context, id, open }: { children: ReactNode; label?: string; context?: string; id?: string; open?: boolean }) {
  return (
    <details className="mon-disclose" id={id} open={open}>
      <summary>{label}{context ? <span className="gp-sr-only"> · {context}</span> : null}</summary>
      {children}
    </details>
  );
}

/** A button the member's role may not use: disabled (focusable, aria-disabled) with "Ask …" under it. */
export function GatedButton({ cap, ask, onClick, children, variant = 'secondary', compact, busy }: {
  cap: Capability; ask: string | null; onClick: () => void; children: ReactNode; variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; compact?: boolean; busy?: boolean;
}) {
  const id = useId();
  if (cap.allowed) {
    return <Button variant={variant} compact={compact} aria-disabled={busy || undefined} onClick={() => { if (!busy) onClick(); }}>{busy ? 'Saving…' : children}</Button>;
  }
  return (
    <span className="mon-cell-stack">
      <Button variant={variant === 'primary' ? 'secondary' : variant} compact={compact} aria-disabled="true" aria-describedby={`${id}-ask`} onClick={() => undefined}>{children}</Button>
      <span id={`${id}-ask`} className="mon-sub">{ask ?? cap.whoCan}</span>
    </span>
  );
}

/** New (signed) · In progress (internal, with its PR / issue link) · Due (sla_due only). */
export function ProgressLines({ row, showDue = true }: {
  showDue?: boolean;
  row: { new_since_pack: boolean; passing: number; of: number; in_progress: { by_name: string | null; since: string; act: string; link: string | null } | null; sla_due: string | null };
}) {
  const fresh = newMarkerText(row);
  const ip = inProgressText(row.in_progress);
  const linkLabel = inProgressLinkLabel(row.in_progress);
  const due = showDue ? dueText(row.sla_due) : null;
  if (!fresh && !ip && !due) return null;
  return (
    <span className="mon-line" data-part="progress">
      {fresh ? <StatusChip label={fresh} tone="blue" glyph="circle" /> : null}
      {ip ? <span data-part="in-progress">{ip}</span> : null}
      {ip && linkLabel && row.in_progress?.link ? <ExternalOrApp href={row.in_progress.link}>{linkLabel}</ExternalOrApp> : null}
      {due ? <span data-part="due">{due}</span> : null}
    </span>
  );
}

/** A link that stays in the app for an app path and opens a new tab for a PR / issue url. */
export function ExternalOrApp({ href, children }: { href: string; children: ReactNode }) {
  if (href.startsWith('/')) return <Link to={href}>{children}</Link>;
  return <a href={href} target="_blank" rel="noreferrer">{children}<span className="gp-sr-only"> (opens in a new tab)</span></a>;
}

/** Member display names (same cache key Today uses); an unknown id reads as nothing, never the id. */
export function useMemberNames(slug: string): ReadonlyMap<string, string> {
  const q = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), retry: false, staleTime: 60_000, enabled: !!slug });
  return useMemo(() => new Map((q.data?.members ?? []).map((m) => [m.user_id, m.display_name || m.email])), [q.data]);
}

/** "hourly", "daily", "every 7 days" from a cadence in seconds. */
export function cadenceLabel(seconds: number | null | undefined): string {
  if (!seconds) return '';
  if (seconds <= 3600) return 'hourly';
  const days = Math.round(seconds / 86_400);
  return days <= 1 ? 'daily' : `every ${days} days`;
}

/**
 * "Run all checks now" (V113-7 round 2): the workspace sweep (owner / admin on the server; 429 within 15 minutes of
 * the last). On success a toast says when results arrive; errors come back in plain words for the caller to show.
 */
export function useRunAllChecks(slug: string) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; details: string; version: number } | null>(null);
  const run = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const out = await startSweep(slug);
      setToast((t) => ({
        message: 'Running all checks',
        details: `${out.collections ? `${out.collections} ${out.collections === 1 ? 'source is' : 'sources are'} being read. ` : ''}Results appear here as each source reports, usually within a few minutes.`,
        version: (t?.version ?? 0) + 1,
      }));
    } catch (e) {
      const f = monitoringErrorOf(e);
      setError(f.status === 429 ? 'All checks ran in the last 15 minutes. Try again shortly.'
        : f.status === 409 ? 'A run of all checks is already under way. Results appear as each source reports.'
          : f.kind === 'forbidden' ? 'Only an owner or admin can run all checks.' : f.message || 'Checks could not be started.');
    } finally { setBusy(false); }
  };
  const toastEl = toast ? <Toast message={toast.message} details={toast.details} version={toast.version} onDismiss={() => setToast(null)} /> : null;
  return { run, busy, error, toast: toastEl, toastMessage: toast ? `${toast.message}. ${toast.details}` : null };
}

/** A config-change cause line ("Prompt-attack filter On → Off · Oct 21, 2026 · See change"); the actor only when recorded. */
export function CauseLine({ cause, seeHref }: { cause: ConfigChangeCause | null | undefined; seeHref?: string | null }) {
  if (!cause) return null;
  return (
    <p className="mon-line" data-part="cause">
      <StatusGlyph kind="dots" size={12} /><span>{causeText(cause)}</span>
      {seeHref ? <Link to={seeHref}>See change</Link> : null}
    </p>
  );
}

type Latest = {
  status: string; label: string | null; observed: string | null; expected: string | null; observed_raw: string | null; check_title: string | null;
  note: string | null; evidence_ref: string | null; observed_at: string;
};

/**
 * The last check in plain words (B-1): `label` in bold ("Prompt-attack filter: Off · expected On"), or "Check failed ·
 * see Verify" when the server has no mapping. Raw facts, the check statement and the evidence sit behind Verify.
 */
export function Observation({ latest, context, hideVerify }: { latest: Latest | null; context?: string; hideVerify?: boolean }) {
  if (!latest) return null;
  const failed = latest.status !== 'pass' && latest.status !== 'not_applicable';
  const href = latest.evidence_ref && /^https?:\/\//.test(latest.evidence_ref) ? latest.evidence_ref : null;
  const hasVerify = !!(latest.observed_raw || latest.evidence_ref || latest.check_title);
  return (
    <span className="mon-cell-stack" data-part="observation">
      {latest.label ? <strong className="mon-obs">{latest.label}</strong>
        : failed ? <span className="mon-obs">Check failed · see Verify</span>
          : latest.note ? <span className="mon-sub">{latest.note}</span> : null}
      {hasVerify && !hideVerify ? (
        <VerifyDisclosure context={context ? `${context} last check` : 'last check'}>
          <dl className="mon-dl">
            <dt>Checked</dt><dd>{fmtDate(latest.observed_at)}</dd>
            {latest.check_title ? <><dt>Check</dt><dd>{latest.check_title}</dd></> : null}
            {latest.observed_raw ? <><dt>Recorded</dt><dd className="mon-mono">{latest.observed_raw}</dd></> : null}
            {latest.note && latest.label ? <><dt>Note</dt><dd>{latest.note}</dd></> : null}
            {latest.evidence_ref ? <><dt>Evidence</dt><dd className="mon-mono">{href ? <a href={href} target="_blank" rel="noreferrer">{href}<span className="gp-sr-only"> (opens in a new tab)</span></a> : latest.evidence_ref}</dd></> : null}
          </dl>
        </VerifyDisclosure>
      ) : null}
    </span>
  );
}
